// Workshop Options Management Component for KATA Dashboard
// Allows admins to manage dropdown options for workshop attributes

import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Search,
  Eye,
  EyeOff,
  AlertCircle 
} from 'lucide-react';
import {
  getOptionsByAttribute,
  createOption,
  updateOption,
  deleteOption,
  getPlantProvisionOptions,
  provisionAlvPlant,
  WorkshopDropdownOption,
  WorkshopDropdownOptionCreate,
  WorkshopDropdownOptionUpdate,
  PlantProvisionOption
} from '../../services/workshopOptions';
import DepartmentOptionsManagement from './DepartmentOptionsManagement';

// Define the 14 attributes (excluding department which has its own table)
const ATTRIBUTES = [
  { value: 'status', label: 'Status' },
  { value: 'sqdcm_category', label: 'SQDCM Category' },
  { value: 'product_line', label: 'Product Line' },
  { value: 'ebit_bridge_lever', label: 'EBIT Bridge Lever' },
  { value: 'impact_type', label: 'Impact Type' },
  { value: 'workstream', label: 'Workstream' },
  { value: 'aps_maturity', label: 'APS Maturity' },
  { value: 'l_gate', label: 'L-Gate' },
  { value: 'priority', label: 'Priority' },
  { value: 'location', label: 'Location' },
  { value: 'alv_id', label: 'ALV ID' },
  { value: 'ws_area', label: 'WS Area' },
  { value: 'category', label: 'Category' },
  { value: 'cc_category', label: 'CC Category' },
  { value: 'department', label: 'Department' },
];

interface FormData {
  value: string;
  label: string;
  code: string;
  display_order: number;
  is_active: boolean;
}

interface ProvisionFormData {
  alv_id: string;
  mode: 'existing' | 'new';
  existing_plant_id: string;
  plant_name: string;
  plant_code: string;
  location: string;
  country: string;
  layout_name: string;
  replace_existing_layout: boolean;
  force_reassign_alv: boolean;
}

const WorkshopOptionsManagement: React.FC = () => {
  const [selectedAttribute, setSelectedAttribute] = useState('status');
  const [options, setOptions] = useState<WorkshopDropdownOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<WorkshopDropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(true);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<WorkshopDropdownOption | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<FormData>({
    value: '',
    label: '',
    code: '',
    display_order: 0,
    is_active: true,
  });
  
  // Error and success messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [plantOptions, setPlantOptions] = useState<PlantProvisionOption[]>([]);
  const [layoutFile, setLayoutFile] = useState<File | null>(null);
  const [provisionData, setProvisionData] = useState<ProvisionFormData>({
    alv_id: '',
    mode: 'existing',
    existing_plant_id: '',
    plant_name: '',
    plant_code: '',
    location: 'Unknown',
    country: 'Unknown',
    layout_name: '',
    replace_existing_layout: false,
    force_reassign_alv: false,
  });

  // Fetch options when attribute changes
  useEffect(() => {
    if (selectedAttribute === 'department') {
      setOptions([]);
      setFilteredOptions([]);
      return;
    }
    fetchOptions();
  }, [selectedAttribute]);

  // Filter options when search term or showInactive changes
  useEffect(() => {
    filterOptions();
  }, [options, searchTerm, showInactive]);

  const fetchOptions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getOptionsByAttribute(selectedAttribute);
      setOptions(data);
    } catch (err) {
      setError('Failed to load options. Please try again.');
      console.error('Error fetching options:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterOptions = () => {
    let filtered = [...options];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(opt => 
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.code && opt.code.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by active status
    if (!showInactive) {
      filtered = filtered.filter(opt => opt.is_active);
    }
    
    // Sort by display_order
    filtered.sort((a, b) => a.display_order - b.display_order);
    
    setFilteredOptions(filtered);
  };

  const handleAddOption = () => {
    const maxOrder = options.length > 0 
      ? Math.max(...options.map(o => o.display_order))
      : 0;
    
    setFormData({
      value: '',
      label: '',
      code: '',
      display_order: maxOrder + 10,
      is_active: true,
    });
    setShowAddModal(true);
    setError(null);
  };

  const handleEditOption = (option: WorkshopDropdownOption) => {
    setSelectedOption(option);
    setFormData({
      value: option.value,
      label: option.label,
      code: option.code || '',
      display_order: option.display_order,
      is_active: option.is_active,
    });
    setShowEditModal(true);
    setError(null);
  };

  const handleDeleteOption = (option: WorkshopDropdownOption) => {
    setSelectedOption(option);
    setShowDeleteModal(true);
    setError(null);
  };

  const handleSubmitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      const newOption: WorkshopDropdownOptionCreate = {
        attribute_name: selectedAttribute,
        value: formData.value,
        label: formData.label,
        code: formData.code || undefined,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };
      
      await createOption(newOption);
      setSuccess('Option added successfully!');
      setShowAddModal(false);
      fetchOptions();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add option. Please try again.');
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOption) return;
    
    setError(null);
    
    try {
      const updateData: WorkshopDropdownOptionUpdate = {
        value: formData.value,
        label: formData.label,
        code: formData.code || undefined,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };
      
      await updateOption(selectedOption.id, updateData);
      setSuccess('Option updated successfully!');
      setShowEditModal(false);
      setSelectedOption(null);
      fetchOptions();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update option. Please try again.');
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedOption) return;
    
    setError(null);
    
    try {
      await deleteOption(selectedOption.id);
      setSuccess('Option deleted successfully!');
      setShowDeleteModal(false);
      setSelectedOption(null);
      fetchOptions();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete option. Please try again.');
    }
  };

  const handleToggleActive = async (option: WorkshopDropdownOption) => {
    try {
      await updateOption(option.id, { is_active: !option.is_active });
      setSuccess(`Option ${option.is_active ? 'deactivated' : 'activated'} successfully!`);
      fetchOptions();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle option status.');
    }
  };

  const handleOpenProvisionModal = async () => {
    setError(null);
    setLayoutFile(null);
    setIsProvisioning(false);
    const firstAlv = (filteredOptions[0]?.value || options[0]?.value || '').toString();
    setProvisionData({
      alv_id: firstAlv,
      mode: 'existing',
      existing_plant_id: '',
      plant_name: '',
      plant_code: '',
      location: 'Unknown',
      country: 'Unknown',
      layout_name: '',
      replace_existing_layout: false,
      force_reassign_alv: false,
    });
    setShowProvisionModal(true);

    try {
      const plants = await getPlantProvisionOptions();
      setPlantOptions(plants);
      if (plants.length > 0) {
        setProvisionData(prev => ({ ...prev, existing_plant_id: String(plants[0].id) }));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load plant options.');
    }
  };

  const handleSubmitProvision = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!provisionData.alv_id.trim()) {
      setError('ALV ID is required.');
      return;
    }

    if (provisionData.mode === 'existing' && !provisionData.existing_plant_id) {
      setError('Please select an existing plant.');
      return;
    }

    if (provisionData.mode === 'new' && (!provisionData.plant_name.trim() || !provisionData.plant_code.trim())) {
      setError('Plant name and plant code are required for new plant mode.');
      return;
    }

    try {
      setIsProvisioning(true);
      const result = await provisionAlvPlant({
        alv_id: provisionData.alv_id.trim(),
        mode: provisionData.mode,
        existing_plant_id: provisionData.mode === 'existing' ? parseInt(provisionData.existing_plant_id, 10) : undefined,
        plant_name: provisionData.mode === 'new' ? provisionData.plant_name.trim() : undefined,
        plant_code: provisionData.mode === 'new' ? provisionData.plant_code.trim() : undefined,
        location: provisionData.mode === 'new' ? provisionData.location.trim() : undefined,
        country: provisionData.mode === 'new' ? provisionData.country.trim() : undefined,
        layout_name: provisionData.layout_name.trim() || undefined,
        replace_existing_layout: provisionData.replace_existing_layout,
        force_reassign_alv: provisionData.force_reassign_alv,
        background_image_file: layoutFile,
      });

      setSuccess(
        `${result.message} Plant=${result.plant.name} (${result.plant.code}), `
        + `layout=${result.layout.name}, workshops remapped=${result.workshops_remapped}.`
      );
      setShowProvisionModal(false);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      const apiError = err?.response?.data;
      if (apiError?.requires_confirmation) {
        setError(`${apiError.error} Enable the confirmation checkboxes and submit again.`);
      } else {
        setError(apiError?.error || err?.message || 'Failed to provision ALV plant mapping.');
      }
    } finally {
      setIsProvisioning(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Workshop Options Management</h2>
        <p className="mt-1 text-sm text-gray-600">
          Configure dropdown options for workshop attributes. Changes take effect immediately.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      )}

      {/* Attribute Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-4 overflow-x-auto" aria-label="Tabs">
          {ATTRIBUTES.map((attr) => (
            <button
              key={attr.value}
              onClick={() => {
                setSelectedAttribute(attr.value);
                setSearchTerm('');
              }}
              className={`
                whitespace-nowrap py-4 px-3 border-b-2 font-medium text-sm
                ${selectedAttribute === attr.value
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {attr.label}
            </button>
          ))}
        </nav>
      </div>

      {selectedAttribute === 'department' ? (
        <DepartmentOptionsManagement />
      ) : (
      <>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search options..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          {selectedAttribute === 'alv_id' && (
            <button
              onClick={handleOpenProvisionModal}
              className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span className="font-medium">Provision Plant Layout</span>
            </button>
          )}

          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors
              ${showInactive
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-gray-100 border-gray-300 text-gray-700'
              }
            `}
          >
            {showInactive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            <span className="text-sm font-medium">
              {showInactive ? 'Showing: All Options' : 'Showing: Active Only'}
            </span>
          </button>
          
          <button
            onClick={handleAddOption}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="font-medium">Add Option</span>
          </button>
        </div>
      </div>

      {/* Options Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            Loading options...
          </div>
        ) : filteredOptions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'No options match your search.' : 'No options found. Add one to get started.'}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Label
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                {selectedAttribute === 'alv_id' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mapped Plant
                  </th>
                )}
                {selectedAttribute === 'alv_id' && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Has Layout
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredOptions.map((option) => (
                <tr
                  key={option.id}
                  className={`
                    hover:bg-gray-50 transition-colors
                    ${!option.is_active ? 'opacity-50' : ''}
                  `}
                >
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">
                    {option.label}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {option.value}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {option.code || '-'}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {option.display_order}
                  </td>
                  {selectedAttribute === 'alv_id' && (
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {option.mapped_plant
                        ? `${option.mapped_plant.name} (${option.mapped_plant.code})`
                        : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Unmapped
                          </span>
                        )}
                    </td>
                  )}
                  {selectedAttribute === 'alv_id' && (
                    <td className="px-4 py-4 text-sm">
                      {option.has_layout ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          No
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleToggleActive(option)}
                      className={`
                        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                        ${option.is_active
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                        }
                      `}
                    >
                      {option.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleEditOption(option)}
                      className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteOption(option)}
                      className="text-red-600 hover:text-red-900 inline-flex items-center ml-3"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Total Count */}
      <div className="text-sm text-gray-600">
        Showing {filteredOptions.length} of {options.length} options
        {!showInactive && ` (${options.filter(o => !o.is_active).length} hidden)`}
        {showInactive && ` (${options.filter(o => o.is_active).length} active, ${options.filter(o => !o.is_active).length} inactive)`}
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleSubmitAdd}>
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Add New Option</h3>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attribute
                  </label>
                  <input
                    type="text"
                    value={ATTRIBUTES.find(a => a.value === selectedAttribute)?.label}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Display label"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Stored value"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 213100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="add-is-active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="add-is-active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Add Option
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOption && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <form onSubmit={handleSubmitEdit}>
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Edit Option</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOption(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attribute
                  </label>
                  <input
                    type="text"
                    value={ATTRIBUTES.find(a => a.value === selectedAttribute)?.label}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Label <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-gray-500">(Display text - safe to edit)</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value <span className="text-red-500">*</span>
                    <span className="ml-2 text-xs text-red-600">(⚠️ Don't edit if used by workshops)</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="w-full px-3 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-yellow-50"
                  />
                  <p className="mt-1 text-xs text-gray-600">
                    This is the stored database value. Changing it will create orphaned references in existing workshops.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code (Optional)
                    <span className="ml-2 text-xs text-gray-500">(Prefix for display - safe to edit)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 213100"
                  />
                  <p className="mt-1 text-xs text-gray-600">
                    Used for formatting: "{formData.code || 'CODE'} - {formData.label}"
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                    <span className="ml-2 text-xs text-gray-500">(Sort position - safe to edit)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-600">
                    Lower numbers appear first in dropdowns
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit-is-active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="edit-is-active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedOption(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Provision Plant Modal */}
      {showProvisionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmitProvision}>
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Provision ALV to Plant Layout</h3>
                <button
                  type="button"
                  onClick={() => setShowProvisionModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isProvisioning}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ALV ID <span className="text-red-500">*</span>
                  </label>
                  {options.length > 0 ? (
                    <select
                      value={provisionData.alv_id}
                      onChange={(e) => setProvisionData({ ...provisionData, alv_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">Select ALV</option>
                      {options.map((opt) => (
                        <option key={opt.id} value={opt.value}>
                          {opt.label} ({opt.value})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={provisionData.alv_id}
                      onChange={(e) => setProvisionData({ ...provisionData, alv_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="e.g. SWN"
                      required
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provision Mode
                  </label>
                  <select
                    value={provisionData.mode}
                    onChange={(e) => setProvisionData({ ...provisionData, mode: e.target.value as 'existing' | 'new' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="existing">Use existing plant</option>
                    <option value="new">Create new plant</option>
                  </select>
                </div>

                {provisionData.mode === 'existing' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Existing Plant <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={provisionData.existing_plant_id}
                      onChange={(e) => setProvisionData({ ...provisionData, existing_plant_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    >
                      <option value="">Select plant</option>
                      {plantOptions.map((plant) => (
                        <option key={plant.id} value={plant.id}>
                          {plant.name} ({plant.code}) {plant.alv_id ? ` - mapped: ${plant.alv_id}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plant Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={provisionData.plant_name}
                        onChange={(e) => setProvisionData({ ...provisionData, plant_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plant Code <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={provisionData.plant_code}
                        onChange={(e) => setProvisionData({ ...provisionData, plant_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      <input
                        type="text"
                        value={provisionData.location}
                        onChange={(e) => setProvisionData({ ...provisionData, location: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={provisionData.country}
                        onChange={(e) => setProvisionData({ ...provisionData, country: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Layout Name</label>
                  <input
                    type="text"
                    value={provisionData.layout_name}
                    onChange={(e) => setProvisionData({ ...provisionData, layout_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Defaults to '<Plant Name> - Main Layout'"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Background Image (optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLayoutFile(e.target.files?.[0] || null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={provisionData.replace_existing_layout}
                      onChange={(e) => setProvisionData({ ...provisionData, replace_existing_layout: e.target.checked })}
                      className="h-4 w-4 mt-1 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Replace existing layout if the selected plant already has one
                    </span>
                  </label>

                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={provisionData.force_reassign_alv}
                      onChange={(e) => setProvisionData({ ...provisionData, force_reassign_alv: e.target.checked })}
                      className="h-4 w-4 mt-1 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Force ALV reassignment when ALV or target plant is already mapped
                    </span>
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowProvisionModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isProvisioning}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProvisioning}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {isProvisioning ? 'Provisioning...' : 'Provision'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOption && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-3">
              <p className="text-gray-700">
                Are you sure you want to delete the option <strong>"{selectedOption.label}"</strong>?
              </p>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Impact on existing workshops:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Workshops using this value will keep it in the database</li>
                      <li>The value won't appear in dropdown filters or forms</li>
                      <li>Existing workshops with this value cannot change back to it</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className="mt-2 text-sm text-red-600 font-medium">
                ⚠️ Recommended: Use the "Deactivate" button instead to hide this option while preserving data integrity.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedOption(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete Option
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default WorkshopOptionsManagement;
